import { dashboardObservability } from "./observability";
import { defineEvent as defineOperationalEvent } from "@diverge/observability/browser";
const operationalLogger = dashboardObservability.logger("dashboard/utils/notify");
const operationalEvents = {
  event1: defineOperationalEvent({
    name: "dashboard.toast.dismiss.rejected",
    message: "Toast dismissal requires a toast ID",
    level: "warn",
    attributes: { "di.reason_code": { type: "category" } },
    classification: "operational",
    volume: "normal",
    owner: "dashboard/src/utils/notify",
  }),
};
import { toast as toastManager } from "@diverge/ui/ui/toast";
import type { ReactNode } from "react";

const DEFAULT_DURATION_SECONDS = 4.5;

/** Toast content accepted by the notify helpers (string, number, or JSX). */
type NotifyContent = ReactNode;

/** Toast id returned by the toast manager; pass back to `destroy(id)` / `dismiss(id)`. */
export type NotifyToastId = string;

/** Extras for a toast. Reusing an `id` updates the visible toast instead of adding one. */
export interface NotifyOptions {
  id?: string;
  description?: ReactNode;
}

type NotifyType = "success" | "error" | "warning" | "info" | "loading";

const NOTIFY_TYPES: readonly string[] = ["success", "error", "warning", "info", "loading"];

function normalizeContent(content: NotifyContent): NotifyContent {
  if (content == null) return "";
  if (typeof content === "string") return content;
  if (typeof content === "number") return String(content);
  return content;
}

/** Seconds to the manager's milliseconds; `0` keeps the toast until it is dismissed. */
function toMs(durationSeconds: number | undefined): number {
  if (durationSeconds === 0) return 0;
  if (typeof durationSeconds !== "number" || Number.isNaN(durationSeconds)) {
    return DEFAULT_DURATION_SECONDS * 1000;
  }
  return Math.round(durationSeconds * 1000);
}

function show(
  type: NotifyType | undefined,
  content: NotifyContent,
  durationSeconds: number | undefined,
  options?: NotifyOptions,
): NotifyToastId {
  return toastManager.add({
    ...options,
    type,
    title: normalizeContent(content),
    timeout: toMs(durationSeconds),
    // Errors interrupt a screen reader; every other toast waits its turn.
    priority: type === "error" ? "high" : "low",
  });
}

/**
 * Each `success/error/warning/info/loading` returns the toast id. Capture this
 * id at call sites and pass it back to `destroy(id)` / `dismiss(id)` to close
 * *only* that toast, instead of blowing away every visible toast in the app.
 */
export function success(
  content: NotifyContent,
  duration: number = DEFAULT_DURATION_SECONDS,
  options?: NotifyOptions,
): NotifyToastId {
  return show("success", content, duration, options);
}

export function error(
  content: NotifyContent,
  duration: number = DEFAULT_DURATION_SECONDS,
  options?: NotifyOptions,
): NotifyToastId {
  return show("error", content, duration, options);
}

export function warning(
  content: NotifyContent,
  duration: number = DEFAULT_DURATION_SECONDS,
  options?: NotifyOptions,
): NotifyToastId {
  return show("warning", content, duration, options);
}

export function info(
  content: NotifyContent,
  duration: number = DEFAULT_DURATION_SECONDS,
  options?: NotifyOptions,
): NotifyToastId {
  return show("info", content, duration, options);
}

export function loading(
  content: NotifyContent,
  duration: number = 0,
  options?: NotifyOptions,
): NotifyToastId {
  return show("loading", content, duration, options);
}

/**
 * Dismiss a single toast by id.
 *
 * Unlike antd's `message.destroy()`, calling this with no id is treated
 * as a usage error: the manager's `close()` without an id wipes every
 * toast (e.g. an in-flight save confirmation) when a caller just wants to
 * close their own loader. Always capture the id returned from
 * `success/error/warning/info/loading` and pass it back here.
 *
 * In development this throws to surface accidental copy-pastes of the
 * antd pattern; in production it no-ops with a shared operational event so a stray
 * call can never take down the entire toast UI.
 */
export function destroy(id?: NotifyToastId | null): void {
  if (id == null) {
    const message =
      "notify.destroy()/dismiss() requires a toast id. " +
      "Capture the id returned from success/error/warning/info/loading() " +
      "and dismiss it explicitly. (Calling with no id used to clear ALL toasts " +
      "globally, which is almost never what you want.)";
    if (process.env.NODE_ENV !== "production") {
      throw new Error(message);
    }
    operationalLogger.emit(operationalEvents.event1, { "di.reason_code": "missing_toast_id" });
    return;
  }
  toastManager.close(id);
}

export function dismiss(id?: NotifyToastId | null): void {
  destroy(id);
}

/** A toast with one action button, e.g. "Open conversation". */
export interface NotifyActionToast {
  /** Fixed per subject, so a repeated notice updates the visible toast. */
  id: string;
  title: ReactNode;
  description?: ReactNode;
  actionLabel: ReactNode;
  /** Runs after the toast closes. */
  onAction: () => void;
  durationSeconds?: number;
}

export function showActionToast({
  id,
  title,
  description,
  actionLabel,
  onAction,
  durationSeconds = 12,
}: NotifyActionToast): NotifyToastId {
  return toastManager.add({
    id,
    title,
    description,
    timeout: toMs(durationSeconds),
    actionProps: {
      children: actionLabel,
      onClick: () => {
        toastManager.close(id);
        onAction();
      },
    },
  });
}

export interface NotifyOpenConfig extends NotifyOptions {
  type?: NotifyType | "default" | (string & {});
  content?: NotifyContent;
  duration?: number;
}

export const message = {
  success,
  error,
  warning,
  info,
  loading,
  /**
   * Always pass the id returned from `loading/success/etc` — see `destroy`.
   */
  destroy,
  dismiss,
  open: (config?: NotifyOpenConfig | null): NotifyToastId | undefined => {
    if (!config) return undefined;
    const { type = "default", content, duration, ...rest } = config;
    const knownType = NOTIFY_TYPES.includes(type) ? (type as NotifyType) : undefined;
    return show(knownType, content, duration, rest);
  },
};

export default message;
